import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Select from "../../../features/ARR/components/shared/Select";
import { URL } from "../../../utils/Constants";
import { axiosPost } from "../../../utils/AxiosApi";
import { Slider } from "@mui/material";

const FilterModal = ({
  type,
  filterData,
  filterModal,
  setFilterData,
  setFilterModal,
  handleFilter,
}) => {
  const [loading, setLoading] = useState({
    educationLevels: false,
    assessments: false,
    students: false,
    yearLevel: false,
    classroom: false,
  });
  const [assessmentOptions, setAssessmentOptions] = useState([]);
  const [educationLevelOptions, setEducationLevelOptions] = useState([]);
  const [studentsOptions, setStudentsOptions] = useState([]);
  const [yearLevelOptions, setYearLevelOptions] = useState([]);
  const [classroomOptions, setClassroomsOptions] = useState([]);
  const filterModalRef = useRef(null);

  const getStudents = useCallback(() => {
    setLoading((prev) => ({ ...prev, students: true }));
    axiosPost(
      URL.students,
      {
        status: "ASSIGNED",
        classRoom: filterData?.classroomId,
        yearLevel: filterData?.yearLevelId,
      },
      (response) => {
        if (response?.status === 200) {
          const results = response.data.data;
          setStudentsOptions(() => {
            return results?.map((item) => ({
              label: item?.peopleDetail?.fullName,
              value: item?.admissionId,
            }));
          });
        }
        setLoading((prev) => ({ ...prev, students: false }));
      },
      (error) => setLoading((prev) => ({ ...prev, students: false })),
    );
  }, [filterData?.classroomId, filterData?.yearLevelId]);

  const getEducationLevel = () => {
    setLoading((prev) => ({ ...prev, educationLevels: true }));
    axiosPost(
      URL.getARRModules,
      {},
      (response) => {
        if (response?.status === 200) {
          const result = response?.data.data;
          const processedData = result?.map((item) => ({
            label: item.name,
            value: item.educationLevelId,
            assessmentModuleCode: item?.code,
          }));
          setEducationLevelOptions(processedData);
        }
        setLoading((prev) => ({ ...prev, educationLevels: false }));
      },
      (error) => setLoading((prev) => ({ ...prev, educationLevels: false })),
    );
  };

  const getAllAssessments = (assessmentModuleCode) => {
    setLoading((prev) => ({ ...prev, assessments: true }));
    axiosPost(
      URL.selectAssessments,
      { assessmentModuleCode: assessmentModuleCode, offset: 0, limit: null },
      (response) => {
        if (response?.status === 200) {
          const results = response?.data?.data;
          const processedData = (results || [])?.map((item) => ({
            label: item?.assessmentName,
            value: item?.id,
          }));
          setAssessmentOptions(processedData);
        }
        setLoading((prev) => ({ ...prev, assessments: false }));
      },
      (error) => setLoading((prev) => ({ ...prev, assessments: false })),
    );
  };

  const getYearLevel = (educationLevelId, assessmentModuleCode) => {
    setLoading((prev) => ({ ...prev, yearLevel: true }));
    axiosPost(
      URL.getYearLevelByEducationLevel,
      {
        assessmentModuleCode: assessmentModuleCode,
        educationLevelId: educationLevelId,
      },
      (response) => {
        if (response?.status === 200) {
          const result = response?.data?.data;
          setYearLevelOptions(() => {
            return result?.map((item) => ({
              label: item.name,
              value: item.id,
            }));
          });
        }
        setLoading((prev) => ({ ...prev, yearLevel: false }));
      },
      (error) => setLoading((prev) => ({ ...prev, yearLevel: false })),
    );
  };

  const getClassrooms = (yearLevelId) => {
    setLoading((prev) => ({ ...prev, classroom: true }));
    axiosPost(
      URL.selectClassroom,
      { yearLevelId: yearLevelId },
      (response) => {
        if (response?.status === 200) {
          const data = response?.data?.data;
          const processedData = data?.map((item) => ({
            label: item?.name,
            value: item?.id,
          }));
          setClassroomsOptions(processedData);
        }
        setLoading((prev) => ({ ...prev, classroom: false }));
      },
      (error) => setLoading((prev) => ({ ...prev, classroom: false })),
    );
  };

  const handleFilterData = (name, value) => {
    if (type === "teacher") {
      if (name === "educationLevelId") {
        setFilterData((prev) => ({
          ...prev,
          [name]: value?.value,
          assessmentId: "",
        }));
        getAllAssessments(value?.assessmentModuleCode);
        return;
      }

      setFilterData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (type === "student") {
      if (name === "educationLevelId") {
        setFilterData((prev) => ({
          ...prev,
          [name]: value?.value,
          assessmentId: "",
          yearLevelId: "",
          classroomId: "",
        }));
        getAllAssessments(value?.assessmentModuleCode);
        getYearLevel(value?.value, value?.assessmentModuleCode);
        return;
      }

      if (name === "yearLevelId") {
        setFilterData((prev) => ({
          ...prev,
          yearLevelId: value,
          classroomId: "",
        }));
        getClassrooms(value);
        return;
      }

      setFilterData((prev) => ({ ...prev, [name]: value }));
      return;
    }
  };

  useEffect(() => {
    if (type !== "student") return;
    getStudents();
  }, [getStudents]);

  useEffect(() => {
    getEducationLevel();
  }, []);

  return (
    <aside
      aria-label="filter-modal"
      className={`filterModal ${filterModal ? "show" : ""}`}
    >
      <div className="flex" style={{ justifyContent: "space-between" }}>
        <h3>Filter</h3>
        <button className="ghost-btn" onClick={() => setFilterModal(false)}>
          <FontAwesomeIcon icon={faXmark} fontSize={16} />
        </button>
      </div>

      <div
        style={{
          marginTop: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        <div className="form-input">
          <label htmlFor="">Education Level</label>

          <Select
            isLoading={loading.educationLevels}
            options={educationLevelOptions}
            value={
              educationLevelOptions?.find(
                (opt) => opt?.value === filterData?.educationLevelId,
              ) || null
            }
            onChange={(option) => {
              handleFilterData("educationLevelId", option);
            }}
            placeholder={"Select Education Level"}
          />
        </div>

        <div className="form-input">
          <label htmlFor="">Assessments</label>
          <Select
            isLoading={loading.assessments}
            options={assessmentOptions}
            value={
              assessmentOptions?.find(
                (opt) => opt?.value === filterData?.assessmentId,
              ) || null
            }
            onChange={(option) =>
              handleFilterData("assessmentId", option?.value)
            }
            placeholder={"Select Assessment"}
            isDisabled={!filterData?.educationLevelId}
          />
        </div>

        {type === "student" && (
          <>
            <div className="form-input">
              <label htmlFor="">Class</label>

              <Select
                isLoading={loading.yearLevel}
                options={yearLevelOptions}
                value={
                  yearLevelOptions?.find(
                    (opt) => opt?.value === filterData?.yearLevelId,
                  ) || null
                }
                onChange={(option) => {
                  handleFilterData("yearLevelId", option?.value);
                }}
                isDisabled={!filterData?.educationLevelId}
                placeholder={"Select class"}
              />
            </div>

            <div className="form-input">
              <label htmlFor="">Section</label>

              <Select
                isLoading={loading.classroom}
                options={classroomOptions}
                value={
                  classroomOptions?.find(
                    (opt) => opt?.value === filterData?.classroomId,
                  ) || null
                }
                onChange={(option) => {
                  handleFilterData("classroomId", option?.value);
                }}
                placeholder={"Select section"}
                isDisabled={!filterData?.yearLevelId}
              />
            </div>

            <div className="form-input">
              <label htmlFor="">Student</label>

              <Select
                isLoading={loading.students}
                options={studentsOptions}
                value={
                  studentsOptions?.find(
                    (opt) => opt?.value === filterData?.admissionId,
                  ) || null
                }
                onChange={(option) => {
                  handleFilterData("admissionId", option?.value);
                }}
                isSearchable
                placeholder={"Select student"}
              />
            </div>
          </>
        )}

        <button
          onClick={handleFilter}
          className="primary-btn"
          style={{ width: "100%" }}
        >
          Submit
        </button>
      </div>
    </aside>
  );
};

export default FilterModal;
